(() => {
    "use strict";

    const SELECTORS = {
        controller: "[data-product-reviews-controller]",
        drawer: "[data-product-reviews-drawer]",
        drawerInner: "[data-product-reviews-drawer-inner]",
        open: "[data-product-reviews-open]",
        close: "[data-product-reviews-close]",
        tab: "[data-product-reviews-tab]",
        panel: "[data-product-reviews-panel]",
        reviewsContent: "[data-product-reviews-content]",
        reviewsLoading: "[data-product-reviews-loading]",
        formContent: "[data-product-review-form-content]",
        formLoading: "[data-product-review-form-loading]",

        reviewsWidget: "#fulfilled-reviews-all",
        klaviyoWriteButton: ".kl_reviews__button",
        klaviyoForm: ".kl_reviews_submission__onsite_card",
        klaviyoClose: ".kl_reviews__close_button",
        klaviyoOverlay:
            ".kl_reviews__submission_modal__overlay",

        klaviyoFrame:
            ".kl_reviews__submission_modal__frame",
    };

    class ProductReviewsDrawer {
        constructor(root) {
            this.root = root;

            this.drawer = root.querySelector(
                SELECTORS.drawer
            );

            this.drawerInner = root.querySelector(
                SELECTORS.drawerInner
            );

            this.openButtons = root.querySelectorAll(
                SELECTORS.open
            );

            this.closeButton = root.querySelector(
                SELECTORS.close
            );

            this.tabs = root.querySelectorAll(
                SELECTORS.tab
            );

            this.panels = root.querySelectorAll(
                SELECTORS.panel
            );

            this.reviewsContent = root.querySelector(
                SELECTORS.reviewsContent
            );

            this.reviewsLoading = root.querySelector(
                SELECTORS.reviewsLoading
            );

            this.formContent = root.querySelector(
                SELECTORS.formContent
            );

            this.formLoading = root.querySelector(
                SELECTORS.formLoading
            );

            this.triggerButton = null;
            this.reviewsWidget = null;
            this.klaviyoOverlay = null;
            this.klaviyoFrame = null;

            this.isOpen = false;
            this.isClosing = false;
            this.formRequestPromise = null;

            this.handleOpen =
                this.handleOpen.bind(this);

            this.handleClose =
                this.handleClose.bind(this);

            this.handleTabClick =
                this.handleTabClick.bind(this);

            this.handleDrawerClick =
                this.handleDrawerClick.bind(this);

            this.handleKeydown =
                this.handleKeydown.bind(this);
        }

        init() {
            if (
                !this.drawer ||
                !this.drawerInner
            ) {
                return;
            }

            this.openButtons.forEach((button) => {
                button.addEventListener(
                    "click",
                    this.handleOpen
                );
            });

            this.closeButton?.addEventListener(
                "click",
                this.handleClose
            );

            this.tabs.forEach((tab) => {
                tab.addEventListener(
                    "click",
                    this.handleTabClick
                );
            });

            this.drawer.addEventListener(
                "click",
                this.handleDrawerClick
            );

            this.drawer.addEventListener(
                "keydown",
                this.handleKeydown
            );
        }

        async handleOpen(event) {
            event.preventDefault();

            this.triggerButton =
                event.currentTarget;

            window.a11y.lastElement =
                this.triggerButton;

            const initialTab =
                event.currentTarget.dataset
                    .reviewsInitialTab || "reviews";

            this.openDrawer();

            await this.mountReviewsWidget();

            await this.activateTab(
                initialTab
            );
        }

        openDrawer() {
            if (
                this.isOpen ||
                this.isClosing
            ) {
                return;
            }

            this.isOpen = true;

            this.drawer.classList.remove(
                "is-closing"
            );

            this.drawer.removeAttribute(
                "inert"
            );

            if (
                typeof this.drawer.showModal ===
                "function" &&
                !this.drawer.open
            ) {
                this.drawer.showModal();
            } else if (!this.drawer.open) {
                this.drawer.setAttribute(
                    "open",
                    ""
                );
            }

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.drawer.classList.add(
                        "is-open"
                    );
                });
            });

            document.dispatchEvent(
                new CustomEvent(
                    "theme:scroll:lock",
                    {
                        bubbles: true,
                        detail: this.drawer,
                    }
                )
            );

            window.theme
                .waitForAnimationEnd(
                    this.drawerInner
                )
                .then(() => {
                    window.theme.a11y.trapFocus(
                        this.drawer,
                        {
                            elementToFocus:
                                this.closeButton,
                        }
                    );
                });
        }

        async handleClose(event) {
            event?.preventDefault?.();

            await this.closeDrawer();
        }

        async closeDrawer() {
            if (
                !this.isOpen ||
                this.isClosing
            ) {
                return;
            }

            this.isClosing = true;

            this.closeNativeKlaviyoForm();

            this.drawer.classList.add(
                "is-closing"
            );

            this.drawer.classList.remove(
                "is-open"
            );

            await window.theme.waitForAnimationEnd(
                this.drawerInner
            );

            if (
                typeof this.drawer.close ===
                "function" &&
                this.drawer.open
            ) {
                this.drawer.close();
            } else {
                this.drawer.removeAttribute(
                    "open"
                );
            }

            this.drawer.classList.remove(
                "is-closing"
            );

            this.drawer.setAttribute(
                "inert",
                ""
            );

            this.isOpen = false;
            this.isClosing = false;

            window.theme.a11y.removeTrapFocus();

            document.dispatchEvent(
                new CustomEvent(
                    "theme:scroll:unlock",
                    {
                        bubbles: true,
                    }
                )
            );

            requestAnimationFrame(() => {
                this.triggerButton?.focus?.();
                this.triggerButton = null;
            });
        }

        handleDrawerClick(event) {
            if (event.target === this.drawer) {
                this.closeDrawer();
            }
        }

        handleKeydown(event) {
            if (event.code !== "Escape") {
                return;
            }

            event.preventDefault();
            this.closeDrawer();
        }

        handleTabClick(event) {
            const tab =
                event.currentTarget.dataset
                    .productReviewsTab;

            this.activateTab(tab);
        }

        async activateTab(tabName) {
            const normalizedTab =
                tabName === "form"
                    ? "form"
                    : "reviews";

            this.tabs.forEach((tab) => {
                const isActive =
                    tab.dataset
                        .productReviewsTab ===
                    normalizedTab;

                tab.classList.toggle(
                    "is-active",
                    isActive
                );

                tab.setAttribute(
                    "aria-selected",
                    String(isActive)
                );

                tab.tabIndex = isActive
                    ? 0
                    : -1;
            });

            this.panels.forEach((panel) => {
                const isActive =
                    panel.dataset
                        .productReviewsPanel ===
                    normalizedTab;

                panel.classList.toggle(
                    "is-active",
                    isActive
                );

                panel.hidden =
                    !isActive;
            });

            if (normalizedTab === "reviews") {
                await this.mountReviewsWidget();
                return;
            }

            await this.mountReviewForm();
        }

        async mountReviewsWidget() {
            if (
                this.reviewsWidget &&
                this.reviewsContent.contains(
                    this.reviewsWidget
                )
            ) {
                return true;
            }

            const widget =
                await this.waitForElement(
                    SELECTORS.reviewsWidget,
                    10000
                );

            if (!widget) {
                if (this.reviewsLoading) {
                    this.reviewsLoading.textContent =
                        "No fue posible cargar las opiniones.";
                }

                return false;
            }

            this.reviewsWidget = widget;

            const sourceSection =
                widget.closest(
                    ".shopify-section"
                );

            sourceSection?.classList.add(
                "product-reviews-source--mounted"
            );

            this.reviewsContent.appendChild(
                widget
            );

            widget.hidden = false;

            if (this.reviewsLoading) {
                this.reviewsLoading.hidden =
                    true;
            }

            return true;
        }

        async mountReviewForm() {
            if (
                this.klaviyoForm &&
                this.klaviyoForm.isConnected
            ) {
                if (
                    !this.formContent.contains(
                        this.klaviyoForm
                    )
                ) {
                    this.formContent.appendChild(
                        this.klaviyoForm
                    );
                }

                this.prepareEmbeddedForm(
                    this.klaviyoForm
                );

                this.formLoading.hidden = true;

                return true;
            }

            if (this.formRequestPromise) {
                return this.formRequestPromise;
            }

            this.formRequestPromise =
                this.requestAndMountForm();

            try {
                return await this.formRequestPromise;
            } finally {
                this.formRequestPromise = null;
            }
        }

        findKlaviyoWriteButton() {
            if (!this.reviewsWidget) {
                return null;
            }

            const directButton =
                this.reviewsWidget.querySelector(
                    ".kl_reviews__button"
                );

            if (directButton) {
                return directButton;
            }

            return Array.from(
                this.reviewsWidget.querySelectorAll(
                    "button, [role='button']"
                )
            ).find((element) => {
                const text =
                    element.textContent
                        ?.trim()
                        .toLowerCase() || "";

                return (
                    text.includes("escribir una reseña") ||
                    text.includes("deja una reseña") ||
                    text.includes("dejar una reseña") ||
                    text.includes("añade una opinión") ||
                    text.includes("write a review")
                );
            }) || null;
        }

        findKlaviyoModalRoot(form) {
            if (!form) {
                return null;
            }

            let element =
                form.parentElement;

            let bestCandidate =
                null;

            while (
                element &&
                element !== document.body
            ) {
                const styles =
                    window.getComputedStyle(element);

                const rect =
                    element.getBoundingClientRect();

                const coversViewport =
                    rect.width >=
                    window.innerWidth * 0.8 &&
                    rect.height >=
                    window.innerHeight * 0.8;

                const isOverlayPosition =
                    styles.position === "fixed" ||
                    styles.position === "absolute";

                if (
                    isOverlayPosition &&
                    coversViewport
                ) {
                    /*
                     * Conservamos el candidato más alto que todavía
                     * pertenece al popup generado por Klaviyo.
                     */
                    bestCandidate =
                        element;
                }

                element =
                    element.parentElement;
            }

            /*
             * Si no detectamos un overlay de pantalla completa,
             * usamos el padre inmediato de la tarjeta.
             */
            return (
                bestCandidate ||
                form.parentElement ||
                form
            );
        }

        prepareEmbeddedKlaviyoRoot(root) {
            if (!root) {
                return;
            }

            root.classList.add(
                "product-reviews-drawer__klaviyo-root"
            );

            /*
             * Neutralizamos estilos inline típicos del portal.
             * El CSS con !important hará el resto.
             */
            root.style.removeProperty(
                "position"
            );

            root.style.removeProperty(
                "inset"
            );

            root.style.removeProperty(
                "top"
            );

            root.style.removeProperty(
                "right"
            );

            root.style.removeProperty(
                "bottom"
            );

            root.style.removeProperty(
                "left"
            );

            root.style.removeProperty(
                "width"
            );

            root.style.removeProperty(
                "height"
            );

            root.style.removeProperty(
                "z-index"
            );

            root.style.removeProperty(
                "background"
            );

            root.style.removeProperty(
                "pointer-events"
            );

            const form =
                root.matches(
                    ".kl_reviews_submission__onsite_card"
                )
                    ? root
                    : root.querySelector(
                        ".kl_reviews_submission__onsite_card"
                    );

            if (!form) {
                return;
            }

            this.klaviyoForm = form;

            form.classList.add(
                "product-reviews-drawer__klaviyo-form"
            );

            form.removeAttribute("inert");
            form.removeAttribute(
                "aria-hidden"
            );

            /*
             * Dentro del drawer ya no necesitamos el botón
             * de cerrar propio del modal de Klaviyo.
             */
            form
                .querySelector(
                    ".kl_reviews__close_button"
                )
                ?.setAttribute(
                    "hidden",
                    ""
                );
        }

        observeEmbeddedKlaviyoForm() {
            if (this.klaviyoRootObserver) {
                return;
            }

            this.klaviyoRootObserver =
                new MutationObserver(() => {
                    const currentForm =
                        document.querySelector(
                            ".kl_reviews_submission__onsite_card"
                        );

                    if (!currentForm) {
                        return;
                    }

                    /*
                     * Ya está correctamente dentro del drawer.
                     */
                    if (
                        this.formContent.contains(
                            currentForm
                        )
                    ) {
                        return;
                    }

                    /*
                     * Klaviyo volvió a renderizar su portal.
                     * Capturamos nuevamente el root vivo.
                     */
                    const newRoot =
                        this.findKlaviyoModalRoot(
                            currentForm
                        ) || currentForm;

                    this.klaviyoRoot =
                        newRoot;

                    this.formContent.appendChild(
                        newRoot
                    );

                    this.prepareEmbeddedKlaviyoRoot(
                        newRoot
                    );
                });

            this.klaviyoRootObserver.observe(
                document.body,
                {
                    childList: true,
                    subtree: true,
                }
            );
        }

        waitForKlaviyoOverlay(
            timeout = 10000
        ) {
            const findOverlay = () =>
                document.querySelector(
                    SELECTORS.klaviyoOverlay
                );

            const current =
                findOverlay();

            if (current) {
                return Promise.resolve(current);
            }

            return new Promise((resolve) => {
                let timer = null;

                const observer =
                    new MutationObserver(() => {
                        const overlay =
                            findOverlay();

                        if (!overlay) {
                            return;
                        }

                        observer.disconnect();

                        if (timer) {
                            window.clearTimeout(timer);
                        }

                        resolve(overlay);
                    });

                observer.observe(
                    document.body,
                    {
                        childList: true,
                        subtree: true,
                    }
                );

                timer = window.setTimeout(() => {
                    observer.disconnect();
                    resolve(findOverlay());
                }, timeout);
            });
        }

        prepareEmbeddedKlaviyoFrame(
            overlay
        ) {
            if (!overlay) {
                return;
            }

            overlay.classList.add(
                "product-reviews-drawer__klaviyo-overlay"
            );

            overlay.removeAttribute("style");

            const frame =
                overlay.querySelector(
                    SELECTORS.klaviyoFrame
                );

            if (!frame) {
                return;
            }

            this.klaviyoFrame = frame;

            frame.classList.add(
                "product-reviews-drawer__klaviyo-frame"
            );

            frame.removeAttribute("style");

            /*
             * El iframe ya tiene su propio title de Klaviyo.
             */
            frame.setAttribute(
                "loading",
                "eager"
            );
        }

        async requestAndMountForm() {
            await this.mountReviewsWidget();

            /*
             * Si el iframe ya está montado en el drawer,
             * no necesitamos volver a abrir Klaviyo.
             */
            if (
                this.klaviyoOverlay?.isConnected &&
                this.formContent.contains(
                    this.klaviyoOverlay
                )
            ) {
                this.prepareEmbeddedKlaviyoFrame(
                    this.klaviyoOverlay
                );

                this.formLoading.hidden = true;

                return true;
            }

            this.formLoading.hidden = false;
            this.formLoading.textContent =
                "Cargando formulario...";

            /*
             * Escuchamos antes de hacer click para no perder
             * una inserción inmediata del iframe.
             */
            const overlayPromise =
                this.waitForKlaviyoOverlay(10000);

            const nativeWriteButton =
                this.findKlaviyoWriteButton();

            if (!nativeWriteButton) {
                this.formLoading.textContent =
                    "No fue posible encontrar el formulario.";

                console.warn(
                    "[ProductReviewsDrawer] No se encontró el botón nativo de Klaviyo."
                );

                return false;
            }

            document.documentElement.classList.add(
                "product-reviews-capturing-form"
            );

            nativeWriteButton.click();

            const overlay =
                await overlayPromise;

            if (!overlay) {
                document.documentElement.classList.remove(
                    "product-reviews-capturing-form"
                );

                this.formLoading.textContent =
                    "No fue posible cargar el formulario.";

                console.warn(
                    "[ProductReviewsDrawer] Klaviyo no insertó el iframe del formulario."
                );

                return false;
            }

            this.klaviyoOverlay = overlay;

            this.klaviyoFrame =
                overlay.querySelector(
                    SELECTORS.klaviyoFrame
                );

            /*
             * Movemos el componente vivo de Klaviyo,
             * no copiamos su HTML.
             */
            this.formContent.appendChild(
                overlay
            );

            this.prepareEmbeddedKlaviyoFrame(
                overlay
            );

            document.documentElement.classList.remove(
                "product-reviews-capturing-form"
            );

            this.formLoading.hidden = true;

            return true;
        }

        findKlaviyoWriteButton() {
            if (!this.reviewsWidget) {
                return null;
            }

            const selectors = [
                ".kl_reviews__button",
                "[data-testid='write-review-button']",
                "[class*='write_review']",
                "[class*='write-review']",
                "button",
            ];

            for (const selector of selectors) {
                const buttons = Array.from(
                    this.reviewsWidget.querySelectorAll(
                        selector
                    )
                );

                const matchingButton =
                    buttons.find((button) => {
                        const text =
                            button.textContent
                                ?.trim()
                                .toLowerCase() || "";

                        return (
                            button.matches(
                                ".kl_reviews__button"
                            ) ||
                            text.includes(
                                "escribir una reseña"
                            ) ||
                            text.includes(
                                "deja una reseña"
                            ) ||
                            text.includes(
                                "dejar una reseña"
                            ) ||
                            text.includes(
                                "añade una opinión"
                            ) ||
                            text.includes(
                                "write a review"
                            )
                        );
                    });

                if (matchingButton) {
                    return matchingButton;
                }
            }

            return null;
        }

        waitForKlaviyoForm(timeout = 10000) {
            const selectors = [
                ".kl_reviews_submission__onsite_card",
                "[class*='kl_reviews_submission__onsite_card']",
                "[class*='kl_review_capture']",
            ];

            const findForm = () => {
                for (const selector of selectors) {
                    const candidate =
                        document.querySelector(selector);

                    if (!candidate) {
                        continue;
                    }

                    /*
                     * Si encontramos un elemento interior de captura,
                     * buscamos el card principal.
                     */
                    return (
                        candidate.closest(
                            ".kl_reviews_submission__onsite_card"
                        ) ||
                        candidate
                    );
                }

                return null;
            };

            const existing = findForm();

            if (existing) {
                return Promise.resolve(existing);
            }

            return new Promise((resolve) => {
                let timer = null;

                const observer =
                    new MutationObserver(() => {
                        const form = findForm();

                        if (!form) {
                            return;
                        }

                        observer.disconnect();

                        if (timer) {
                            window.clearTimeout(timer);
                        }

                        resolve(form);
                    });

                observer.observe(
                    document.body,
                    {
                        childList: true,
                        subtree: true,
                    }
                );

                timer = window.setTimeout(() => {
                    observer.disconnect();
                    resolve(null);
                }, timeout);
            });
        }

        prepareEmbeddedForm(form) {
            form.classList.add(
                "product-reviews-drawer__klaviyo-form"
            );

            form.removeAttribute("style");

            /*
             * El drawer ya tiene su propio botón de cierre.
             */
            const closeButton =
                form.querySelector(
                    ".kl_reviews__close_button"
                );

            if (closeButton) {
                closeButton.hidden = true;
            }

            /*
             * Klaviyo puede agregar atributos de modal.
             * Dentro del drawer ya no son necesarios.
             */
            form.removeAttribute("role");
            form.removeAttribute("aria-modal");

            /*
             * En caso de que el formulario tuviera inert
             * por haber sido parte de otro modal.
             */
            form.removeAttribute("inert");
            form.removeAttribute("aria-hidden");
        }

        removeEmptyKlaviyoWrapper(startingElement) {
            let element = startingElement;

            while (
                element &&
                element !== document.body &&
                !this.formContent.contains(element)
            ) {
                const parent =
                    element.parentElement;

                const containsForm =
                    element.querySelector?.(
                        ".kl_reviews_submission__onsite_card"
                    );

                /*
                 * El formulario ya fue movido. Si el wrapper
                 * quedó vacío o solo contiene capas auxiliares,
                 * se elimina para evitar un segundo overlay.
                 */
                if (
                    !containsForm &&
                    element.children.length === 0
                ) {
                    element.remove();
                } else if (
                    !containsForm &&
                    getComputedStyle(element).position ===
                    "fixed"
                ) {
                    element.classList.add(
                        "product-reviews-klaviyo-overlay--hidden"
                    );
                }

                element = parent;
            }
        }

        hideKlaviyoOverlay() {
            if (!this.klaviyoForm) {
                return;
            }

            let parent =
                this.klaviyoForm.parentElement;

            while (
                parent &&
                parent !== document.body &&
                !parent.matches(
                    "[data-product-review-form-content]"
                )
            ) {
                const style =
                    getComputedStyle(parent);

                if (
                    style.position === "fixed" &&
                    parent.children.length <= 3
                ) {
                    parent.classList.add(
                        "product-reviews-klaviyo-wrapper"
                    );
                }

                parent =
                    parent.parentElement;
            }
        }

        closeNativeKlaviyoForm() {
            /*
             * Ya movimos el formulario al drawer.
             * No lo eliminamos al cerrar para conservar
             * lo que el usuario haya escrito.
             */
            document
                .querySelectorAll(
                    ".product-reviews-klaviyo-wrapper"
                )
                .forEach((wrapper) => {
                    wrapper.classList.remove(
                        "product-reviews-klaviyo-wrapper"
                    );
                });
        }

        waitForElement(
            selector,
            timeout = 5000
        ) {
            const current =
                document.querySelector(selector);

            if (current) {
                return Promise.resolve(current);
            }

            return new Promise((resolve) => {
                let timer;

                const observer =
                    new MutationObserver(() => {
                        const element =
                            document.querySelector(
                                selector
                            );

                        if (!element) {
                            return;
                        }

                        observer.disconnect();
                        clearTimeout(timer);
                        resolve(element);
                    });

                observer.observe(
                    document.documentElement,
                    {
                        childList: true,
                        subtree: true,
                    }
                );

                timer = setTimeout(() => {
                    observer.disconnect();
                    resolve(null);
                }, timeout);
            });
        }

        destroy() {
            this.openButtons.forEach((button) => {
                button.removeEventListener(
                    "click",
                    this.handleOpen
                );
            });

            this.closeButton?.removeEventListener(
                "click",
                this.handleClose
            );

            this.tabs.forEach((tab) => {
                tab.removeEventListener(
                    "click",
                    this.handleTabClick
                );
            });

            this.drawer.removeEventListener(
                "click",
                this.handleDrawerClick
            );

            this.drawer.removeEventListener(
                "keydown",
                this.handleKeydown
            );

            this.klaviyoRootObserver
                ?.disconnect();

            this.klaviyoRootObserver =
                null;
        }
    }

    function initialize(
        container = document
    ) {
        const roots = [];

        if (
            container.matches?.(
                SELECTORS.controller
            )
        ) {
            roots.push(container);
        }

        roots.push(
            ...(
                container.querySelectorAll?.(
                    SELECTORS.controller
                ) || []
            )
        );

        roots.forEach((root) => {
            if (
                root.dataset
                    .reviewsDrawerInitialized ===
                "true"
            ) {
                return;
            }

            root.dataset
                .reviewsDrawerInitialized =
                "true";

            const controller =
                new ProductReviewsDrawer(root);

            controller.init();

            root.productReviewsDrawer =
                controller;
        });
    }

    if (
        document.readyState ===
        "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            () => initialize(),
            { once: true }
        );
    } else {
        initialize();
    }

    document.addEventListener(
        "shopify:section:load",
        (event) => {
            initialize(event.target);
        }
    );
})();